import React, { useState, useEffect } from 'react';
// TODO: Update to use useMemberContext hook for proper member profile context
import { formatDate as formatDateUtil, formatDateForInput, parseDateOnlyString, formatTimeString, isDateOnly } from '../../utils/dateUtils';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Box,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  DirectionsBike as DirectionsBikeIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  BookOnline as BookOnlineIcon,
  AccessTime as AccessTimeIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Event as EventIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Repeat as RepeatIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { classAPI, classTypeAPI, classLevelAPI, memberProfileAPI, classInstanceAPI, tenantSettingsAPI } from '../../services/api';
import { Class, ClassType, ClassLevel, ClassBooking, MemberProfile, TierAccessLevel, ClassInstance } from '../../types';

// Extended class interface to include user booking status and tier access info
interface ClassWithBookingStatus extends Class {
  userBooking?: ClassBooking;
  isUserBooked: boolean;
  canCancelBooking: boolean;
  tierAccessLevel?: TierAccessLevel;
  isAccessible: boolean; // false if no_access
}

const MemberClassesPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for classes and filters
  const [classes, setClasses] = useState<ClassWithBookingStatus[]>([]);
  const [filteredClasses, setFilteredClasses] = useState<ClassWithBookingStatus[]>([]);
  const [classTypes, setClassTypes] = useState<ClassType[]>([]);
  const [classLevels, setClassLevels] = useState<ClassLevel[]>([]);
  const [userBookings, setUserBookings] = useState<ClassBooking[]>([]);
  const [memberProfile, setMemberProfile] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Booking dialog states
  const [selectedClass, setSelectedClass] = useState<ClassWithBookingStatus | null>(null);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [classInstances, setClassInstances] = useState<ClassInstance[]>([]);
  const [selectedInstances, setSelectedInstances] = useState<string[]>([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [isRecurringClass, setIsRecurringClass] = useState(false);
  
  // Cancellation confirmation dialog states
  const [cancelConfirmDialog, setCancelConfirmDialog] = useState(false);
  const [cancelClassItem, setCancelClassItem] = useState<ClassWithBookingStatus | null>(null);
  const [cancelWarningInfo, setCancelWarningInfo] = useState<{
    willBeCharged: boolean;
    chargeAmount: number;
    message: string;
  } | null>(null);

  // Fetch data on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        // Fetch member profile first to get tier information
        const selectedProfileData = localStorage.getItem('selectedMemberProfile');
        let profile: MemberProfile | null = null;
        
        if (selectedProfileData) {
          try {
            const selectedProfile = JSON.parse(selectedProfileData);
            const selectedProfileId = selectedProfile.id;
            const profileResponse = await memberProfileAPI.getMemberProfileById(selectedProfileId);
            profile = profileResponse.data;
            setMemberProfile(profile);
          } catch (err) {
            console.error('Error fetching member profile:', err);
          }
        }
        
        // Fetch all data in parallel
        const [classesResponse, typesResponse, levelsResponse, bookingsResponse] = await Promise.all([
          classAPI.getClasses(),
          classTypeAPI.getAll(),
          classLevelAPI.getAll(),
          classAPI.getUserBookings()
        ]);

        // Process classes
        const activeUpcomingClasses = classesResponse.data.filter((classItem: Class) => {
          const classDate = new Date(classItem.scheduleDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return classItem.active && classDate >= today;
        });

        // Set other data
        setClassTypes(typesResponse.data.filter((type: ClassType) => type.active));
        setClassLevels(levelsResponse.data.filter((level: ClassLevel) => level.active));
        setUserBookings(bookingsResponse.data);

        // Merge classes with bookings and tier access
        const classesWithBookings = mergeClassesWithBookings(activeUpcomingClasses, bookingsResponse.data, profile);
        setClasses(classesWithBookings);

      } catch (error: any) {
        console.error('Error fetching initial data:', error);
        setError('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Filter classes when search term or filters change
  useEffect(() => {
    filterClasses();
  }, [classes, searchTerm, selectedType, selectedLevel, selectedDate, selectedDayOfWeek]);

  const fetchUserBookings = async () => {
    try {
      const response = await classAPI.getUserBookings();
      setUserBookings(response.data);
    } catch (error: any) {
      console.error('Error fetching user bookings:', error);
      // Don't set error for bookings failure, as it's not critical
    }
  };

  const mergeClassesWithBookings = (classesData: Class[], bookingsData: ClassBooking[], profile: MemberProfile | null): ClassWithBookingStatus[] => {
    console.log('Merging classes with bookings:', {
      classesCount: classesData.length,
      bookingsCount: bookingsData.length,
      bookings: bookingsData.map(b => ({ classId: b.classId, status: b.status, id: b.id })),
      membershipTier: profile?.membershipTier?.name
    });

    return classesData.map(classItem => {
      const userBooking = bookingsData.find(booking => {
        if (booking.status === 'cancelled') return false;
        // If this class card represents a specific upcoming instance (recurring class),
        // only match a booking that is for that exact instance.
        // This prevents stale bookings for past instances from appearing as active
        // on future-instance cards.
        if (classItem.nextInstanceId) {
          return booking.classInstanceId === classItem.nextInstanceId;
        }
        // Non-recurring / legacy: match by parent class ID
        return booking.classId === classItem.id;
      });
      
      const isUserBooked = !!userBooking;
      
      // Determine tier access level for this class
      const tierName = profile?.membershipTier?.name;
      let tierAccessLevel: TierAccessLevel = TierAccessLevel.FREE; // Default to free
      let isAccessible = true;
      
      if (tierName && classItem.tierAccess) {
        const configuredAccess = classItem.tierAccess[tierName];
        if (configuredAccess) {
          tierAccessLevel = configuredAccess as TierAccessLevel;
          isAccessible = tierAccessLevel !== TierAccessLevel.NO_ACCESS;
        }
      }
      
      // Use proper date/time parsing from dateUtils
      let classDateTime;
      try {
        // Check if scheduleDate is date-only format (YYYY-MM-DD)
        if (isDateOnly(classItem.scheduleDate)) {
          // For date-only strings, parse without timezone conversion
          const [year, month, day] = classItem.scheduleDate.split('-').map(Number);
          const date = new Date(year, month - 1, day); // Month is 0-indexed
          
          // Parse the time using the same logic as formatTimeString
          const timeOnly = classItem.startTime.split('+')[0].split('-')[0]; // Remove timezone if present
          const [hours, minutes, seconds] = timeOnly.split(':').map(Number);
          
          // Set the time on the date
          date.setHours(hours, minutes || 0, seconds || 0, 0);
          classDateTime = date;
        } else {
          // For timestamps, use normal parsing then adjust time
          const date = new Date(classItem.scheduleDate);
          const timeOnly = classItem.startTime.split('+')[0].split('-')[0];
          const [hours, minutes, seconds] = timeOnly.split(':').map(Number);
          date.setHours(hours, minutes || 0, seconds || 0, 0);
          classDateTime = date;
        }
        
        // If parsing failed, fallback
        if (isNaN(classDateTime.getTime())) {
          console.warn('Failed to parse class date/time, using date only');
          classDateTime = new Date(classItem.scheduleDate);
        }
      } catch (error) {
        console.error('Error parsing class date/time:', error);
        classDateTime = new Date(classItem.scheduleDate); // Fallback to just date
      }
      
      const now = new Date();
      const canCancelBooking = isUserBooked && classDateTime > now;
      
      return {
        ...classItem,
        userBooking,
        isUserBooked,
        canCancelBooking,
        tierAccessLevel,
        isAccessible
      };
    });
  };

  const refreshData = async (updatedBookings?: ClassBooking[]) => {
    try {
      setLoading(true);
      
      // Fetch classes and bookings
      const [classesResponse, bookingsResponse] = await Promise.all([
        classAPI.getClasses(),
        updatedBookings ? Promise.resolve({ data: updatedBookings }) : classAPI.getUserBookings()
      ]);

      // Process classes
      const activeUpcomingClasses = classesResponse.data.filter((classItem: Class) => {
        const classDate = new Date(classItem.scheduleDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return classItem.active && classDate >= today;
      });
      
      // Set bookings state if we fetched new ones
      if (!updatedBookings) {
        setUserBookings(bookingsResponse.data);
      }

      // Merge classes with bookings and tier access
      const bookingsToUse = updatedBookings || bookingsResponse.data;
      const classesWithBookings = mergeClassesWithBookings(activeUpcomingClasses, bookingsToUse, memberProfile);
      setClasses(classesWithBookings);

      console.log('Refreshed data - Classes with bookings:', classesWithBookings.filter(c => c.isUserBooked).length);

    } catch (error: any) {
      console.error('Error refreshing data:', error);
      setError('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassTypes = async () => {
    try {
      const response = await classTypeAPI.getAll();
      setClassTypes(response.data.filter((type: ClassType) => type.active));
    } catch (error: any) {
      console.error('Error fetching class types:', error);
    }
  };

  const fetchClassLevels = async () => {
    try {
      const response = await classLevelAPI.getAll();
      setClassLevels(response.data.filter((level: ClassLevel) => level.active));
    } catch (error: any) {
      console.error('Error fetching class levels:', error);
    }
  };

  const filterClasses = () => {
    let filtered = classes;

    // Filter by search term (class name, instructor, or description)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((classItem: ClassWithBookingStatus) =>
        classItem.name.toLowerCase().includes(searchLower) ||
        (classItem.instructorName || classItem.instructor || '').toLowerCase().includes(searchLower) ||
        classItem.description?.toLowerCase().includes(searchLower) ||
        classItem.venue.toLowerCase().includes(searchLower)
      );
    }

    // Filter by type
    if (selectedType) {
      filtered = filtered.filter((classItem: ClassWithBookingStatus) => classItem.type === selectedType);
    }

    // Filter by level
    if (selectedLevel) {
      filtered = filtered.filter((classItem: ClassWithBookingStatus) => classItem.level === selectedLevel);
    }

    // Filter by date
    if (selectedDate) {
      // Format date without timezone conversion
      const selectedDateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
      filtered = filtered.filter((classItem: ClassWithBookingStatus) => {
        // Extract date part from timestamp if needed (e.g., "2026-02-02T05:00:00.000Z" -> "2026-02-02")
        const classDateOnly = classItem.scheduleDate.split('T')[0];
        return classDateOnly === selectedDateStr;
      });
    }

    // Filter by day of week
    if (selectedDayOfWeek) {
      filtered = filtered.filter((classItem: ClassWithBookingStatus) => {
        // Extract date part from timestamp if needed (e.g., "2026-02-02T05:00:00.000Z" -> "2026-02-02")
        const dateOnly = classItem.scheduleDate.split('T')[0];
        // Parse date as YYYY-MM-DD without timezone conversion
        const [year, month, day] = dateOnly.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        return dayOfWeek.toString() === selectedDayOfWeek;
      });
    }

    // Sort by date and time
    filtered.sort((a, b) => {
      // Handle time with timezone (e.g., "20:00:00+00") or without timezone (e.g., "20:00:00")
      const timeOnlyA = a.startTime.split('+')[0].split('-')[0]; // Remove timezone if present
      const timeOnlyB = b.startTime.split('+')[0].split('-')[0]; // Remove timezone if present
      
      const dateA = new Date(`${a.scheduleDate}T${timeOnlyA}`);
      const dateB = new Date(`${b.scheduleDate}T${timeOnlyB}`);
      return dateA.getTime() - dateB.getTime();
    });

    setFilteredClasses(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedType('');
    setSelectedLevel('');
    setSelectedDate(null);
    setSelectedDayOfWeek('');
  };

  const handleBookClass = async (classItem: ClassWithBookingStatus) => {
    setSelectedClass(classItem);
    setSelectedInstances([]);
    setError(null);
    
    // Check if this is a recurring class
    const recurring = !!(classItem.recurringPattern && classItem.recurringPattern.daysOfWeek && classItem.recurringPattern.daysOfWeek.length > 0);
    setIsRecurringClass(recurring);
    
    // Always fetch instances - both recurring and non-recurring classes have instances
    await fetchClassInstances(classItem, classItem.id);
    
    setBookingDialog(true);
  };

  const fetchClassInstances = async (classItem: ClassWithBookingStatus, classId: string) => {
    if (!classItem) return;
    
    try {
      setLoadingInstances(true);
      setError(null); // Clear previous errors
      
      // Check if member has a tier
      if (!memberProfile?.membershipTier) {
        setError('You do not have a membership tier assigned. Please contact an administrator.');
        setClassInstances([]);
        return;
      }
      
      const tierName = memberProfile.membershipTier.name;
      
      // Check if booking windows are configured for this class
      if (!classItem.bookingWindows || Object.keys(classItem.bookingWindows).length === 0) {
        setError('Booking windows are not configured for this class. Please contact an administrator.');
        setClassInstances([]);
        return;
      }
      
      // Check if booking window is defined for this tier
      const bookingWindow = classItem.bookingWindows[tierName];
      if (!bookingWindow) {
        setError(`Booking window is not configured for membership tier "${tierName}". Please contact an administrator.`);
        setClassInstances([]);
        return;
      }
      
      // Calculate end date based on booking window from configured open time
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let endDate: Date;
      
      if (bookingWindow.unlimited) {
        // Unlimited booking - show next 3 months
        endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 3);
      } else {
        // Limited booking - show only within allowed window from configured open time
        // Fetch bookingOpenTime from settings
        try {
          const settingsResponse = await tenantSettingsAPI.getSetting('bookingOpenTime');
          const bookingOpenTime = settingsResponse.data || '00:01';
          const [hours, minutes] = bookingOpenTime.split(':').map(Number);
          const todayOpenTime = new Date();
          todayOpenTime.setHours(hours, minutes, 0, 0);
          endDate = new Date(todayOpenTime);
          endDate.setDate(endDate.getDate() + bookingWindow.advanceBookingDays);
        } catch (error) {
          // Fallback to midnight if settings fetch fails
          endDate = new Date();
          endDate.setDate(endDate.getDate() + bookingWindow.advanceBookingDays);
        }
      }
      
      const response = await classInstanceAPI.getInstances({
        parentClassId: classId,
        startDate: today.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        status: 'SCHEDULED',
        includeBookings: 'true'
      });
      
      const instances = response.data || [];
      setClassInstances(instances);
      
      // Auto-select single instance for non-recurring classes
      if (instances.length === 1) {
        setSelectedInstances([instances[0].id]);
      }
    } catch (error: any) {
      console.error('Error fetching class instances:', error);
      setError('Failed to fetch class instances');
    } finally {
      setLoadingInstances(false);
    }
  };

  const handleInstanceSelection = (instanceId: string, selected: boolean) => {
    if (selected) {
      setSelectedInstances(prev => [...prev, instanceId]);
    } else {
      setSelectedInstances(prev => prev.filter(id => id !== instanceId));
    }
  };

  const handleSelectAllInstances = () => {
    if (selectedInstances.length === classInstances.length) {
      setSelectedInstances([]);
    } else {
      setSelectedInstances(classInstances.map(instance => instance.id));
    }
  };

  const confirmBooking = async () => {
    if (!selectedClass) return;
    
    // For both recurring and non-recurring, require at least one instance selected
    if (selectedInstances.length === 0) {
      setError('Please select at least one class instance');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use instance-based booking for all classes (both recurring and non-recurring)
      const registrations: Array<{error?: string; instanceId?: string; message?: string}> = [];
      for (const instanceId of selectedInstances) {
        try {
          const response = await classInstanceAPI.bookInstance(instanceId, {
            notes: ''
          });
          registrations.push(response.data);
        } catch (error: any) {
          console.error(`Error booking instance ${instanceId}:`, error);
          registrations.push({ 
            error: error.response?.data?.message || 'Booking failed',
            instanceId 
          });
        }
      }
      
      // Check for errors
      const hasErrors = registrations.some(r => r.error);
      if (!hasErrors) {
        setBookingDialog(false);
        setSelectedClass(null);
      }
      
      // Show results
      const successCount = registrations.filter(r => !r.error).length;
      const errorCount = registrations.filter(r => r.error).length;
      const errorMessages = registrations.filter(r => r.error).map(r => r.error).join(', ');
      
      if (errorCount === 0) {
        setSuccessMessage(`Successfully booked ${successCount} class instance${successCount > 1 ? 's' : ''}!`);
      } else if (successCount > 0) {
        setSuccessMessage(`Booked ${successCount} instance${successCount > 1 ? 's' : ''}, but ${errorCount} booking${errorCount > 1 ? 's' : ''} failed. Errors: ${errorMessages}`);
      } else {
        setError(`All bookings failed: ${errorMessages}`);
        return; // Don't close dialog
      }
      
      // Refresh data
      const bookingResponse = await classAPI.getUserBookings();
      setUserBookings(bookingResponse.data);
      refreshData(bookingResponse.data);
    } catch (error: any) {
      console.error('Error booking class:', error);
      const errorMessage = error.response?.data?.message || 'Failed to book class';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (classItem: ClassWithBookingStatus) => {
    if (!classItem.userBooking) return;

    try {
      // First, check the cancellation policy for this booking
      const policyResponse = await classAPI.getCancellationPolicyForBooking(classItem.id, classItem.userBooking.id);
      const { canCancelWithoutPenalty, isWithinPenaltyFreeWindow, hoursUntilClass, cancelWithoutPenaltyHrs } = policyResponse.data;
      const paidAmount = classItem.userBooking.paidAmount || 0;
      const willBeCharged = paidAmount > 0 && !isWithinPenaltyFreeWindow;
      
      // If the member will be charged, show confirmation dialog BEFORE cancelling
      if (willBeCharged) {
        setCancelClassItem(classItem);
        setCancelWarningInfo({
          willBeCharged: true,
          chargeAmount: classItem.userBooking.paidAmount || 0,
          message: `The cancellation period has passed (${cancelWithoutPenaltyHrs} hours before class). You will be charged $${Number(classItem.userBooking.paidAmount || 0).toFixed(2)} for this class. Continue with cancellation?`
        });
        setCancelConfirmDialog(true);
        return; // Wait for user confirmation
      }
      
      // No charge - proceed with cancellation
      await performCancellation(classItem);
    } catch (error: any) {
      console.error('Error checking cancellation policy:', error);
      // If policy check fails, still allow cancellation but warn user
      const msg = error.response?.data?.message || 'Failed to check cancellation policy';
      setErrorMessage(msg);
    }
  };
  
  const performCancellation = async (classItem: ClassWithBookingStatus): Promise<boolean> => {
    if (!classItem.userBooking) return false;
    
    try {
      const response = await classAPI.cancelClassBooking(classItem.id, classItem.userBooking.id);
      setSuccessMessage(response.data.message || 'Class booking cancelled successfully');
      
      // Refresh user bookings first, then refresh classes with new booking data
      const bookingResponse = await classAPI.getUserBookings();
      setUserBookings(bookingResponse.data);
      refreshData(bookingResponse.data);
      return true;
    } catch (error: any) {
      console.error('Error cancelling booking:', error);
      const msg = error.response?.data?.message || 'Failed to cancel booking';
      setErrorMessage(msg);
      return false;
    }
  };
  
  const handleConfirmCancellation = async () => {
    if (cancelClassItem) {
      const success = await performCancellation(cancelClassItem);
      if (!success) return; // Keep dialog open so user sees the error
    }
    setCancelConfirmDialog(false);
    setCancelClassItem(null);
    setCancelWarningInfo(null);
  };

  const formatTime = (timeString: string) => {
    return formatTimeString(timeString);
  };

  // const formatDate = (dateString: string) => {
  //   const date = new Date(dateString);
  //   const today = new Date();
  //   const tomorrow = new Date(today);
  //   tomorrow.setDate(tomorrow.getDate() + 1);

  //   if (date.toDateString() === today.toDateString()) {
  //     return 'Today';
  //   } else if (date.toDateString() === tomorrow.toDateString()) {
  //     return 'Tomorrow';
  //   } else {
  //     return date.toLocaleDateString('en-US', { 
  //       weekday: 'long', 
  //       month: 'short', 
  //       day: 'numeric' 
  //     });
  //   }
  // };

  const formatRecurringPattern = (classItem: Class) => {
    if (!classItem.recurringPattern || !classItem.recurringPattern.daysOfWeek || classItem.recurringPattern.daysOfWeek.length === 0) {
      return null;
    }
    
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const days = classItem.recurringPattern.daysOfWeek.map((day: number) => daysOfWeek[day]).join(', ');
    
    return `Occurs every week on ${days}`;
  };

  const getAvailabilityStatus = (classItem: ClassWithBookingStatus) => {
    // Backend must provide availableSpots
    if (classItem.availableSpots === undefined) {
      console.error('availableSpots not provided by backend for class:', classItem.id);
      return { text: 'Error', color: 'error' as const };
    }
    
    const available = classItem.availableSpots;
    const percentageAvailable = (available / classItem.capacity) * 100;
    
    if (available <= 0) {
      // If class is full and waitlist is available, show "Waitlist"
      // If class is full and no waitlist, show "Full"
      if (classItem.isWaitlistAvailable) {
        return { text: 'Waitlist', color: 'warning' as const };
      }
      return { text: 'Full', color: 'error' as const };
    }
    
    // More than 50% spots available
    if (percentageAvailable > 50) {
      return { text: 'Available', color: 'success' as const };
    }
    
    // Less than 50% spots available
    return { text: 'Limited', color: 'warning' as const };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
          <DirectionsBikeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Upcoming Classes
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Book your spot in upcoming fitness classes
        </Typography>
      </Paper>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: isMobile ? 2 : 3 }}>
          <Stack spacing={2}>
            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder="Search classes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              size={isMobile ? 'small' : 'medium'}
            />

            {/* Filter Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                variant={showFilters ? 'contained' : 'outlined'}
                size="small"
              >
                Filters
              </Button>
              
              {(selectedType || selectedLevel || selectedDate || selectedDayOfWeek) && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  size="small"
                  color="secondary"
                >
                  Clear
                </Button>
              )}

              {/* Active Filter Tags */}
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {selectedType && (
                  <Chip 
                    label={`Type: ${selectedType}`} 
                    onDelete={() => setSelectedType('')}
                    size="small"
                  />
                )}
                {selectedLevel && (
                  <Chip 
                    label={`Level: ${selectedLevel}`} 
                    onDelete={() => setSelectedLevel('')}
                    size="small"
                  />
                )}
                {selectedDate && (
                  <Chip 
                    label={`Date: ${formatDateUtil(`${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`)}`} 
                    onDelete={() => setSelectedDate(null)}
                    size="small"
                  />
                )}
                {selectedDayOfWeek && (
                  <Chip 
                    label={`Day: ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][parseInt(selectedDayOfWeek)]}`} 
                    onDelete={() => setSelectedDayOfWeek('')}
                    size="small"
                  />
                )}
              </Stack>
            </Box>

            {/* Expanded Filters */}
            {showFilters && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Class Type</InputLabel>
                    <Select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      label="Class Type"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      {classTypes.map((type) => (
                        <MenuItem key={type.id} value={type.name}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Class Level</InputLabel>
                    <Select
                      value={selectedLevel}
                      onChange={(e) => setSelectedLevel(e.target.value)}
                      label="Class Level"
                    >
                      <MenuItem value="">All Levels</MenuItem>
                      {classLevels.map((level) => (
                        <MenuItem key={level.id} value={level.name}>
                          {level.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Day of Week</InputLabel>
                    <Select
                      value={selectedDayOfWeek}
                      onChange={(e) => setSelectedDayOfWeek(e.target.value)}
                      label="Day of Week"
                    >
                      <MenuItem value="">All Days</MenuItem>
                      <MenuItem value="1">Monday</MenuItem>
                      <MenuItem value="2">Tuesday</MenuItem>
                      <MenuItem value="3">Wednesday</MenuItem>
                      <MenuItem value="4">Thursday</MenuItem>
                      <MenuItem value="5">Friday</MenuItem>
                      <MenuItem value="6">Saturday</MenuItem>
                      <MenuItem value="0">Sunday</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Class Date"
                      value={selectedDate}
                      onChange={(newDate) => setSelectedDate(newDate)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          placeholder: 'Select a date'
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Classes Grid */}
      {filteredClasses.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No classes found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || selectedType || selectedLevel
                ? 'Try adjusting your filters to see more classes'
                : 'Check back later for new classes'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredClasses.map((classItem) => {
            const availability = getAvailabilityStatus(classItem);
            const isNoAccess = !classItem.isAccessible;
            
            return (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={classItem.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    opacity: isNoAccess ? 0.6 : 1,
                    backgroundColor: isNoAccess ? 'action.disabledBackground' : 'background.paper',
                    '&:hover': {
                      transform: isNoAccess ? 'none' : 'translateY(-2px)',
                      boxShadow: isNoAccess ? 'none' : theme.shadows[4]
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                        {classItem.name}
                      </Typography>
                      <Chip
                        label={availability.text}
                        color={availability.color}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    
                    {/* Class Details */}
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DirectionsBikeIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {classItem.type} • {classItem.level}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {classItem.instructorName || classItem.instructor}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOnIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {classItem.venue}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                          {formatDateUtil(classItem.scheduleDate)}
                        </Typography>
                      </Box>
                      
                      {formatRecurringPattern(classItem) && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <RepeatIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'medium' }}>
                            {formatRecurringPattern(classItem)}
                          </Typography>
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatTime(classItem.startTime)} - {formatTime(classItem.endTime)}
                        </Typography>
                      </Box>
                      
                      {classItem.tierAccessLevel === TierAccessLevel.PAID && classItem.oneTimeCost ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AttachMoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                            ${classItem.oneTimeCost} per class
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AttachMoneyIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                          <Typography variant="body2" color="success.main" sx={{ fontWeight: 'medium' }}>
                            Free
                          </Typography>
                        </Box>
                      )}
                      
                      {classItem.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                          {classItem.description}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                  
                  <Divider />
                  
                  <CardActions sx={{ p: 2 }}>
                    {isNoAccess ? (
                      // No access tier - show disabled message
                      <Button
                        variant="outlined"
                        disabled
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}
                        sx={{ 
                          color: 'text.disabled',
                          borderColor: 'action.disabled'
                        }}
                      >
                        Not Accessible
                      </Button>
                    ) : classItem.isUserBooked ? (
                      // User is already booked - show status and cancel option
                      <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                        <Button
                          variant="outlined"
                          startIcon={<CheckCircleIcon />}
                          disabled
                          sx={{ flexGrow: 1 }}
                          size={isMobile ? 'small' : 'medium'}
                          color="success"
                        >
                          {classItem.userBooking?.status === 'waitlist' 
                            ? `Waitlist #${classItem.userBooking?.waitlistPosition}`
                            : 'Registered'
                          }
                        </Button>
                        {classItem.canCancelBooking && (
                          <Button
                            variant="outlined"
                            color="error"
                            size={isMobile ? 'small' : 'medium'}
                            onClick={() => handleCancelBooking(classItem)}
                            startIcon={<CancelIcon />}
                          >
                            Cancel
                          </Button>
                        )}
                      </Stack>
                    ) : (
                      // User is not booked - show book button
                      <Button
                        variant="contained"
                        startIcon={<BookOnlineIcon />}
                        onClick={() => handleBookClass(classItem)}
                        disabled={availability.text === 'Full'}
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}
                      >
                        {availability.text === 'Full' ? 'Class Full' : 
                         availability.text === 'Waitlist' ? 'Join Waitlist' : 
                         'Book Now'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Booking Confirmation Dialog */}
      <Dialog
        open={bookingDialog}
        onClose={() => !loading && setBookingDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BookOnlineIcon />
            <Box>
              <Typography variant="h6">
                Confirm Class Booking
              </Typography>
              {selectedClass && (
                <Typography variant="body2" color="text.secondary">
                  {selectedClass.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {selectedClass && (
            <Box>
              {!isRecurringClass ? (
                // Single class booking
                <>
                  <DialogContentText sx={{ mb: 2 }}>
                    Are you sure you want to book a spot in this class?
                  </DialogContentText>
                  
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Instructor:</strong> {selectedClass.instructorName || selectedClass.instructor}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Date:</strong> {formatDateUtil(selectedClass.scheduleDate)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Time:</strong> {formatTime(selectedClass.startTime)} - {formatTime(selectedClass.endTime)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Location:</strong> {selectedClass.venue}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Cost:</strong> {selectedClass.tierAccessLevel === TierAccessLevel.PAID && selectedClass.oneTimeCost 
                            ? `$${Number(selectedClass.oneTimeCost).toFixed(2)}` 
                            : 'Free (included in membership)'}
                        </Typography>
                        {selectedClass.tierAccessLevel === TierAccessLevel.PAID && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            You will be charged for this class booking based on your membership tier access.
                          </Alert>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </>
              ) : (
                // Recurring class - show instance selection
                <>
                  {/* Booking Window Info */}
                  {selectedClass && memberProfile?.membershipTier && selectedClass.bookingWindows && (
                    (() => {
                      const tierName = memberProfile.membershipTier.name;
                      const bookingWindow = selectedClass.bookingWindows[tierName];
                      
                      if (bookingWindow) {
                        return (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            {bookingWindow.unlimited 
                              ? `Your "${tierName}" membership allows unlimited advance booking for this class.`
                              : `Your "${tierName}" membership allows booking up to ${bookingWindow.advanceBookingDays} days in advance for this class.`
                            }
                          </Alert>
                        );
                      }
                      return null;
                    })()
                  )}
                  
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" component="div">
                        {classInstances.length === 1 
                          ? 'Class Instance' 
                          : `Select Classes (${selectedInstances.length} selected)`}
                      </Typography>
                      {classInstances.length > 1 && (
                        <Button
                          size="small"
                          onClick={handleSelectAllInstances}
                          disabled={loadingInstances || classInstances.length === 0}
                        >
                          {selectedInstances.length === classInstances.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      )}
                    </Box>
                    
                    {loadingInstances ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : classInstances.length === 0 ? (
                      <Alert severity="warning">
                        No upcoming class instances available to book.
                      </Alert>
                    ) : (
                      <List sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        {classInstances.map((instance) => {
                          const instanceAvailable = instance.capacity - (instance.enrolledCount || 0);
                          const instancePercentage = (instanceAvailable / instance.capacity) * 100;
                          
                          return (
                            <ListItem key={instance.id} divider >
                              <ListItemIcon>
                                <Checkbox
                                  checked={selectedInstances.includes(instance.id)}
                                  onChange={(e) => handleInstanceSelection(instance.id, e.target.checked)}
                                />
                              </ListItemIcon>
                              {/* <ListItemIcon>
                                <CalendarTodayIcon sx={{ color: 'text.secondary' }} />
                              </ListItemIcon> */}
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2">
                                      {formatDateUtil(instance.sessionDate)}
                                    </Typography>
                                  <Chip
                                      size="small"
                                      label={
                                        instanceAvailable <= 0 
                                          ? (instance.isWaitlistAvailable ? 'Waitlist' : 'Full')
                                          : instancePercentage > 50 
                                          ? 'Available' 
                                          : 'Limited'
                                      }
                                      color={
                                        instanceAvailable <= 0
                                          ? instance.isWaitlistAvailable ? 'warning' : 'error'
                                          : instancePercentage > 50
                                          ? 'success'
                                          : 'warning'
                                      }
                                      variant="outlined"
                                    />
                                    
                                    <Typography variant="body2" color="text.secondary" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                       <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                       {formatTimeString(instance.startTime)} - {formatTimeString(instance.endTime)}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Box>
                  
                  {/* Booking Summary */}
                  {selectedInstances.length > 0 && (
                    <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          Booking Summary
                        </Typography>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Instances selected:</strong> {selectedInstances.length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Cost per instance:</strong> {
                              selectedClass.tierAccessLevel === TierAccessLevel.PAID && selectedClass.oneTimeCost
                                ? `$${Number(selectedClass.oneTimeCost).toFixed(2)}`
                                : 'Free (included in membership)'
                            }
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Total cost:</strong> {
                              selectedClass.tierAccessLevel === TierAccessLevel.PAID && selectedClass.oneTimeCost
                                ? `$${(Number(selectedClass.oneTimeCost) * selectedInstances.length).toFixed(2)}`
                                : 'Free (included in membership)'
                            }
                          </Typography>
                          {selectedClass.tierAccessLevel === TierAccessLevel.PAID && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                              You will be charged for these class bookings based on your membership tier access.
                            </Alert>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setBookingDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmBooking} 
            variant="contained"
            disabled={loading || (isRecurringClass && selectedInstances.length === 0)}
            startIcon={loading ? <CircularProgress size={20} /> : <BookOnlineIcon />}
          >
            {loading 
              ? 'Booking...' 
              : isRecurringClass 
                ? `Book ${selectedInstances.length} Class${selectedInstances.length !== 1 ? 'es' : ''}`
                : 'Confirm Booking'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancellation Confirmation Dialog (with late fee warning) */}
      <Dialog
        open={cancelConfirmDialog}
        onClose={() => setCancelConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Cancellation</DialogTitle>
        <DialogContent>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}
          {cancelClassItem && cancelWarningInfo && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {cancelWarningInfo.message}
              </Alert>
              
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {cancelClassItem.name}
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Date:</strong> {formatDateUtil(cancelClassItem.scheduleDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Time:</strong> {formatTime(cancelClassItem.startTime)}
                    </Typography>
                    <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                      <strong>Charge Amount:</strong> ${Number(cancelWarningInfo.chargeAmount).toFixed(2)}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
              
              <DialogContentText sx={{ mt: 2 }}>
                Do you want to proceed with the cancellation?
              </DialogContentText>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmDialog(false)}>
            Keep Booking
          </Button>
          <Button 
            onClick={handleConfirmCancellation} 
            variant="contained" 
            color="error"
          >
            Cancel Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MemberClassesPage;
